import 'knockout.validation';
import ko from 'knockout';

import { DateTimeViewModel } from '../date-time-view-model';
import { EmailViewModel } from '../email-view-model';
import {
	initCpr,
	initDatePicker,
	initPhone,
	initTimePicker,
	getDefaultEndTime,
} from '../form-helpers';
import { httpClient, HTTPClientError } from '../httpClient';
import {
	CreateMeetingRequest,
	MeetingCreationResult,
	Status,
} from '../interfaces';
import { isCpr } from '../is-cpr';
import { LocaleUtils } from '../locale-utils';
import { Log } from '../log';
import { PhoneViewModel } from '../phone-view-model';
import { R } from '../Resources.resx';

export class CreateMeetingWithNemIdViewModel {
	private _validator: KnockoutValidationGroup;
	private _isEndDateTimeManuallySet = false;
	private _endDatePicker: any = null;
	private _endTimePicker: any = null;

	public errors = ko.observableArray([]);
	public success = ko.observable(false);
	public cpr = ko.observable<string>();
	public startDateTime = new DateTimeViewModel();
	public endDateTime = new DateTimeViewModel();
	public participantPhoneNumbers = ko.observableArray<PhoneViewModel>([
		new PhoneViewModel(),
	]);

	public participantEmails = ko.observableArray<EmailViewModel>([
		new EmailViewModel(),
	]);

	public validParticipantPhoneNumbers = ko.pureComputed(() => {
		return this.participantPhoneNumbers().filter((p) => p.isValid());
	});

	public validParticipantEmails = ko.pureComputed(() => {
		return this.participantEmails().filter((p) => p.isValid());
	});

	public participantPhoneNumbersFormatted = ko.pureComputed(() => {
		return this.participantPhoneNumbers()
			.map((p) => p.value())
			.join(', ');
	});

	public participantEmailsFormatted = ko.pureComputed(() => {
		return this.participantEmails()
			.map((p) => p.value())
			.join(', ');
	});

	public hasPhoneOrEmail = ko.pureComputed(() => {
		return (
			this.validParticipantPhoneNumbers().length > 0 ||
			this.validParticipantEmails().length > 0
		);
	});

	public isEndTimeValid = ko.pureComputed(() => {
		const end = this.endDateTime.dateTime();
		const start = this.startDateTime.dateTime();
		return end != null && start != null && end > start;
	});

	public isSubmitting = ko.observable(false);

	public timeZone = LocaleUtils.getCurrentTimeZoneLabel();

	constructor() {
		this.initDatepicker();
		initCpr('[name=cpr]');

		this.cpr.extend({
			required: {
				message: R.ErrorFieldIsMandatory,
				param: true,
			},
			validation: {
				validator: isCpr,
				message: R.ErrorInvalidCpr,
			},
		});

		// also validate the nested ViewModels
		ko.validation.init({
			grouping: { deep: true },
		});

		this._validator = ko.validatedObservable([this.cpr]);

		this._validator.errors.showAllMessages(false);

		this.startDateTime.dateTime.subscribe((start) => {
			if (!start || this._isEndDateTimeManuallySet) return;
			const newEnd = getDefaultEndTime(start);
			this.endDateTime.date(new Date(newEnd));
			this.endDateTime.time(new Date(newEnd));
			this._endDatePicker?.setDate(new Date(newEnd), false);
			this._endTimePicker?.setDate(new Date(newEnd), false);
		});

		this.startDateTime.setCurrentDateAndTime();
	}

	public afterRenderPhoneInput = (): void => {
		initPhone('[name=phone]');
	};

	public reset = (): void => {
		this.success(false);
		this.errors([]);
		this.cpr('');
		this.participantPhoneNumbers([new PhoneViewModel()]);
		this.participantEmails([new EmailViewModel()]);
		this._isEndDateTimeManuallySet = false;
		this.initDatepicker();
		this._validator.errors?.showAllMessages(false);
		this.startDateTime.setCurrentDateAndTime();
	};

	private initDatepicker = (): void => {
		initDatePicker('#withNemIdStartDate', (selectedDates) => {
			this.startDateTime.date(selectedDates[0]);
		});
		initTimePicker('#withNemIdStartTime', (selectedDates) => {
			this.startDateTime.time(selectedDates[0]);
		});
		const defaultEndTime = getDefaultEndTime(new Date());
		this._endDatePicker = initDatePicker(
			'#withNemIdEndDate',
			(selectedDates) => {
				this._isEndDateTimeManuallySet = true;
				this.endDateTime.date(selectedDates[0]);
			},
			defaultEndTime,
		);
		this._endTimePicker = initTimePicker(
			'#withNemIdEndTime',
			(selectedDates) => {
				this._isEndDateTimeManuallySet = true;
				this.endDateTime.time(selectedDates[0]);
			},
			defaultEndTime,
		);
	};

	public createMeeting = (): void => {
		if (!this._validator.isValid()) {
			this._validator.errors?.showAllMessages();
			return;
		}

		if (!this.isEndTimeValid()) {
			return;
		}

		if (this.isSubmitting()) {
			return;
		}

		this.isSubmitting(true);

		const request: CreateMeetingRequest = {
			cpr: this.cpr()?.replace('-', ''),
			participantPhoneNumbers: this.validParticipantPhoneNumbers().map((p) =>
				p.value(),
			),
			participantEmails: this.validParticipantEmails().map((p) => p.value()),
			startTime: this.startDateTime.dateTime(),
			endTime: this.endDateTime.dateTime(),
		};

		httpClient
			.post<Status<MeetingCreationResult>>('/api/meetings', request)
			.then((data) => {
				if (data.hasErrors) {
					this.errors(data.errors.map((e) => e.value));
					return;
				}
				this.success(true);
			})
			.catch((error) => {
				if (error instanceof HTTPClientError) {
					// The request was made and the server responded with a status code
					// that falls out of the range of 2xx
					Log.error(
						'Error creating meeting. ' +
							error.status +
							': ' +
							(error.data ?? error.message),
					);
				} else {
					// Something happened in setting up the request that triggered an Error
					Log.error('Error creating meeting', error.message);
				}
				this.errors([R.ErrorCreateConsultation]);
			})
			.finally(() => {
				this.isSubmitting(false);
			});
	};
}
